export interface SchemaNodeExtension {
  toJson(): object;
}

export interface SchemaNodeJson {
  name: string;
  modelName: string | null;
  childNodes?: SchemaNodeJson[];
  extensions?: object[];
}

export interface SchemaNodeParent {
  name: string;
}

export interface SchemaNodeLike<TModelSchema = unknown> extends SchemaNodeParent {
  childNodes: Array<SchemaNodeLike<TModelSchema>>;
  extensions: Record<string, SchemaNodeExtension>;
  modelName: string | null;
  modelSchema: TModelSchema | undefined;
  parentNode: SchemaNodeParent | null;
  append(_node: SchemaNodeLike<TModelSchema>): void;
  setExtension(_name: string, _extension: SchemaNodeExtension): void;
  getExtension<TExtension extends SchemaNodeExtension = SchemaNodeExtension>(
    _name: string,
  ): TExtension | undefined;
  toJson(): SchemaNodeJson;
}

export class SchemaNode<TModelSchema = unknown> implements SchemaNodeLike<TModelSchema> {
  childNodes: Array<SchemaNodeLike<TModelSchema>>;

  extensions: Record<string, SchemaNodeExtension>;

  modelName: string | null;

  modelSchema: TModelSchema | undefined;

  name: string;

  parentNode: SchemaNodeParent | null;

  constructor(
    name: string,
    modelName: string | null,
    parentNode: SchemaNodeParent | null,
    modelSchema?: TModelSchema,
  ) {
    this.name = name;
    this.childNodes = [];
    this.parentNode = parentNode;
    this.modelName = modelName;
    this.modelSchema = modelSchema;
    this.extensions = {};

    this.append = this.append.bind(this);
    this.setExtension = this.setExtension.bind(this);
    this.getExtension = this.getExtension.bind(this);
  }

  append(node: SchemaNodeLike<TModelSchema>) {
    this.childNodes.push(node);
  }

  setExtension(name: string, extension: SchemaNodeExtension) {
    this.extensions[name] = extension;
  }

  getExtension<TExtension extends SchemaNodeExtension = SchemaNodeExtension>(name: string) {
    return this.extensions[name] as TExtension | undefined;
  }

  toJson(): SchemaNodeJson {
    return {
      name: this.name,
      modelName: this.modelName,
      childNodes: this.childNodes.length > 0 ? this.childNodes.map(child => child.toJson()) : undefined,
      extensions: Object.keys(this.extensions).length > 0
        ? Object.keys(this.extensions).map(key => this.extensions[key].toJson()) : undefined,
    };
  }
}
